
import React, { useEffect, useRef, useMemo } from 'react';
import { Slider } from '@/components/ui/slider';
import { motion, useAnimation } from 'framer-motion';

interface NutriscoreBarProps {
  value: number;
  onChange?: (value: number) => void;
  readonly?: boolean;
  label?: string;
  colorScheme?: 'green' | 'blue' | 'purple' | 'orange';
}

const NutriscoreBar: React.FC<NutriscoreBarProps> = ({ 
  value, 
  onChange,
  readonly = false,
  label,
  colorScheme = 'green' 
}) => {
  // Use useMemo to prevent recalculating this on every render
  const safeValue = useMemo(() => {
    const processedValue = typeof value === 'number' && !isNaN(value) ? 
      Math.max(0, Math.min(100, value)) : 0;
    
    return processedValue;
  }, [value]);
  
  const prevValue = useRef(safeValue);
  const controls = useAnimation();
  const animationScheduled = useRef(false);
  const skipNextAnimation = useRef(false);
  
  useEffect(() => {
    if (prevValue.current !== safeValue && !animationScheduled.current && !skipNextAnimation.current) {
      animationScheduled.current = true;
      
      // Use requestAnimationFrame to batch animations and prevent excessive rendering
      requestAnimationFrame(() => {
        controls.start({
          scale: [1, 1.05, 1],
          transition: { 
            duration: 0.3,
            type: "tween" as const 
          }
        });
        prevValue.current = safeValue;
        animationScheduled.current = false;
      });
    } else if (skipNextAnimation.current) {
      skipNextAnimation.current = false;
      prevValue.current = safeValue;
    }
  }, [safeValue, controls]);
  
  const getNutriscoreColor = (score: number, scheme = colorScheme) => {
    const colorMap = {
      green: {
        low: '#ea384c',
        medium: '#F97316',
        high: '#16a34a'
      },
      blue: {
        low: '#ea384c',
        medium: '#3b82f6',
        high: '#0c4a6e'
      },
      purple: {
        low: '#ea384c',
        medium: '#a855f7',
        high: '#7e22ce'
      },
      orange: {
        low: '#ea384c',
        medium: '#f97316',
        high: '#c2410c'
      }
    };
    
    const colors = colorMap[scheme];
    
    if (score <= 30) return colors.low;
    if (score <= 60) return colors.medium;
    return colors.high;
  };
  
  // Memoize the slider change handler
  const handleSliderChange = React.useCallback((newValue: number[]) => {
    // Skip animation on user-driven changes
    skipNextAnimation.current = true;
    
    // Ensure the value is valid before calling onChange
    if (onChange && newValue && newValue.length > 0 && typeof newValue[0] === 'number' && !isNaN(newValue[0])) {
      onChange(newValue[0]);
    }
  }, [onChange]);

  const getNutriscoreLabel = (score: number) => {
    if (score <= 30) return 'Bajo';
    if (score <= 60) return 'Medio';
    return 'Alto';
  };

  // Memoize the color gradient
  const colorGradient = useMemo(() => {
    const color = getNutriscoreColor(safeValue);
    return `linear-gradient(90deg, ${color}aa, ${color})`;
  }, [safeValue, colorScheme]);
  
  return (
    <motion.div 
      className="space-y-2"
      animate={controls}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          {label || 'Nutriscore'}: {safeValue.toFixed(0)}
        </span>
        <motion.span 
          className="px-2 py-1 text-xs rounded-full text-white"
          style={{ backgroundColor: getNutriscoreColor(safeValue) }}
          initial={{ scale: 0.9 }}
          animate={{ 
            scale: [0.95, 1.05, 0.95],
            transition: { 
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse" as const,
              type: "tween" as const 
            }
          }}
        >
          {getNutriscoreLabel(safeValue)}
        </motion.span>
      </div>
      
      {readonly ? (
        <div className="relative h-3 overflow-hidden rounded-full bg-gray-100">
          <motion.div
            className="absolute h-full rounded-full"
            style={{ 
              background: colorGradient,
              width: `${safeValue}%` 
            }}
            initial={{ width: 0 }}
            animate={{ width: `${safeValue}%` }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          />
        </div>
      ) : (
        <Slider
          value={[safeValue]}
          min={1}
          max={100}
          step={1}
          className="py-2"
          onValueChange={handleSliderChange}
          style={{ 
            '--thumb-bg': getNutriscoreColor(safeValue)
          } as React.CSSProperties}
        />
      )}
    </motion.div>
  );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(NutriscoreBar);
